/** System status → Workers: each pool of media workers, its replicas and what they run now. */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { elapsed, relative } from '../../../lib/format';
import { Dot, Usage, type ResourceUse, type StatusDTO } from './kit';
import { TableScroll } from '../../../components/TableScroll';

export function Workers({ s }: { s: StatusDTO }) {
  const [showQueue, setShowQueue] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} data-testid="workers-view">
      <p className="lede" style={{ margin: 0 }}>
        Heavy work runs in separate worker containers by kind. Every replica of a kind shares its queue, and each task goes to the next free worker, so adding replicas adds parallel throughput.
        Capacity is replicas × concurrent jobs per replica.
      </p>
      {s.workers.map((w) => (
        <section key={w.taskQueue} className="panel-card" data-testid="worker-pool">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0 }}>{w.label}</h3>
            <span className="faint" style={{ fontSize: 12 }}>{w.site}{w.pool !== 'cpu' ? ` · ${w.pool}` : ''}</span>
            {showQueue && <span className="faint mono" style={{ fontSize: 11 }}>{w.taskQueue}</span>}
            <span style={{ flex: 1 }} />
            <Dot ok={w.healthy} label={w.healthy ? `${w.pollers} polling` : 'No workers'} />
          </div>
          <span className="help" style={{ marginTop: -6 }}>{w.help}</span>
          <div className="worker-figures">
            <div><span>Replicas</span><strong>{w.role === 'graphics' ? w.pollers : w.replicas.filter((r) => r.healthy).length}</strong></div>
            {w.role !== 'graphics' && <div><span>Running</span><strong>{w.busy}{w.capacity ? <span className="faint"> / {w.capacity}</span> : null}</strong></div>}
            <div><span>Waiting</span><strong>{w.backlog ?? '—'}</strong>{w.backlogAgeSeconds ? <span className="faint" style={{ fontSize: 11 }}>oldest {elapsed(new Date(Date.now() - w.backlogAgeSeconds * 1000).toISOString(), null)}</span> : null}</div>
            <div><span>Last poll</span><strong style={{ fontSize: 13, fontWeight: 500 }}>{relative(w.lastPollAt)}</strong></div>
          </div>
          {w.replicas.length > 0 && (
            <TableScroll>
            <table className="table compact" data-testid="worker-replicas">
              <thead><tr><th>Replica</th><th>State</th><th>Slots</th><th>Resources</th><th>Running now</th><th>Done · failed</th></tr></thead>
              <tbody>{w.replicas.map((r) => (
                <tr key={r.instance} data-testid="worker-replica">
                  <td className="mono wrap" style={{ fontSize: 12 }}>{r.instance}{r.roles.length > 1 && <div className="faint" style={{ fontSize: 11 }}>also {r.roles.filter((x) => x !== w.role).join(', ')}</div>}</td>
                  <td><Dot ok={r.healthy} label={r.healthy ? `Up ${elapsed(r.startedAt, null)}` : `Silent ${relative(r.lastSeenAt)}`} /></td>
                  <td className="mono">{r.running.length} / {r.concurrency ?? '—'}</td>
                  <td><Usage r={(r as { resources?: ResourceUse | null }).resources ?? undefined} /></td>
                  <td className="wrap" style={{ minWidth: 220 }}>
                    {r.running.length === 0 ? <span className="faint">Idle</span> : r.running.map((j, i) => (
                      <div key={i} className="worker-job" data-testid="worker-job">
                        <span>{j.label}{j.step && j.activity === 'generateDerivative' ? ` ${j.step}` : ''}</span>
                        {j.subject ? <Link to={j.subject.link}>{j.subject.name}</Link> : null}
                        {j.percent != null && <span className="mono faint">{Math.round(j.percent)}%</span>}
                        <span className="faint" style={{ fontSize: 11 }}>{elapsed(j.startedAt, null)}{j.attempt > 1 ? ` · attempt ${j.attempt}` : ''}</span>
                        {j.percent != null && <div className="progress"><span style={{ width: `${j.percent}%` }} /></div>}
                      </div>
                    ))}
                  </td>
                  <td className="mono muted nowrap">{r.completed} · <span style={{ color: r.failed ? 'var(--danger)' : undefined }}>{r.failed}</span></td>
                </tr>
              ))}</tbody>
            </table>
            </TableScroll>
          )}
          {w.role === 'graphics' && <span className="help">Graphics renderers are isolated and do not report to the API; their count comes from Temporal.</span>}
          {w.role !== 'graphics' && w.replicas.length === 0 && w.pollers > 0 && <span className="help">Workers poll this queue but cannot reach the API to report what they run (for example at a remote site).</span>}
        </section>
      ))}
      <span className="help">
        To add replicas: in Docker Compose, <code>--scale transcode-worker=3</code> (or <code>media-worker</code>, <code>transfer-worker</code>); in Kubernetes, the replicas of each entry under <code>mediaWorkers</code> in the Helm values, or its autoscaling.
        {' '}<button className="linkish" onClick={() => setShowQueue(!showQueue)}>{showQueue ? 'Hide' : 'Show'} queue names</button>
      </span>
    </div>
  );
}
